import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { type FetchResourceParams } from '#api/client'
import { useDebounce } from '#hooks/useDebounce'
import { RANGE_MINUTES, type RangeKey } from '#utils/timeRanges'

export interface BaseFilterValues {
  range: RangeKey
  customFrom: string
  customTo: string
  scac: string
}

interface UseReportFiltersOptions<T extends BaseFilterValues> {
  defaultRange?: RangeKey
  debounceMs?: number
  initFromParams: (
    searchParams: URLSearchParams,
  ) => Omit<T, keyof BaseFilterValues>
  toApiFilters?: (filters: T) => Record<string, unknown>
  syncToParams?: (params: URLSearchParams, filters: T) => void
}

export const useReportFilters = <T extends BaseFilterValues>(
  options: UseReportFiltersOptions<T>,
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const toApiFiltersRef = useRef(options.toApiFilters)
  toApiFiltersRef.current = options.toApiFilters

  const syncToParamsRef = useRef(options.syncToParams)
  syncToParamsRef.current = options.syncToParams

  const [localFilters, setLocalFilters] = useState<T>(() => {
    const baseInit: BaseFilterValues = {
      range:
        (searchParams.get('range') as RangeKey) ||
        options.defaultRange ||
        '30d',
      customFrom: searchParams.get('customFrom') || '',
      customTo: searchParams.get('customTo') || '',
      scac: searchParams.get('scac') || '',
    }

    const extraInit = options.initFromParams(searchParams)
    return { ...baseInit, ...extraInit } as T
  })

  const debouncedFilters = useDebounce(localFilters, options.debounceMs ?? 500)

  const toApiFilters = useCallback(
    (filters: T) => toApiFiltersRef.current?.(filters),
    [],
  )

  const syncToParams = useCallback(
    (params: URLSearchParams, filters: T) =>
      syncToParamsRef.current?.(params, filters),
    [],
  )

  const apiFilters = useMemo(() => {
    const now = new Date()
    // Stabilize time to start of current minute to prevent millisecond/second drift
    // from turning identical filters into different query keys and busting React Query cache
    now.setSeconds(0, 0)
    let startDate: string | undefined
    let endDate: string | undefined

    if (debouncedFilters.range === 'all') {
      startDate = undefined
      endDate = undefined
    } else if (debouncedFilters.range === 'custom') {
      startDate = debouncedFilters.customFrom
        ? new Date(debouncedFilters.customFrom).toISOString()
        : undefined
      endDate = debouncedFilters.customTo
        ? new Date(debouncedFilters.customTo).toISOString()
        : undefined
    } else {
      const minutes = RANGE_MINUTES[debouncedFilters.range]
      startDate = new Date(now.getTime() - minutes * 60 * 1_000).toISOString()
      endDate = now.toISOString()
    }

    const filters: FetchResourceParams = {
      startDate,
      endDate,
      scac: debouncedFilters.scac || undefined,
      ...toApiFilters(debouncedFilters),
    }

    return filters
  }, [debouncedFilters, toApiFilters])

  useEffect(() => {
    const params = new URLSearchParams(searchParams)

    params.delete('range')
    params.delete('customFrom')
    params.delete('customTo')
    params.delete('scac')

    if (debouncedFilters.range) params.set('range', debouncedFilters.range)
    if (debouncedFilters.customFrom)
      params.set('customFrom', debouncedFilters.customFrom)
    if (debouncedFilters.customTo)
      params.set('customTo', debouncedFilters.customTo)
    if (debouncedFilters.scac) params.set('scac', debouncedFilters.scac)

    syncToParams(params, debouncedFilters)

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true })
    }
  }, [debouncedFilters, searchParams, setSearchParams, syncToParams])

  return { localFilters, setLocalFilters, apiFilters }
}
